<template>
  <component
    :is="DataTable"
    :loading="isLoading"
    v-model:filters="filters"
    :paginator="paginator"
    v-model:selection="selectedRows"
    :lazy="lazy"
    :totalRecords="lazy ? totalRecords : undefined"
    v-model:first="first"
    v-model:rows="rowsPerPage"
    @page="onPage"
    @sort="onSort"
    :rowsPerPageOptions="[10, 20, 30, 50, 100]"
    :pt="{
     paginator: {
       rowPerPageDropdown: {
         panel: { class: 'panel-row-per-page-dropdown' },
       },
      },
    }"
    showGridlines
    stripedRows
    scrollable
    scrollHeight="100px"
    removableSort
    ref="datatable"
    :sortField="lazy ? sortFieldValue : 'Id'"
    :sortOrder="lazy ? sortOrderValue : -1"
    :value="tableItems"
    paginator-template="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
    current-page-report-template="Exibir {first} a {last} de {totalRecords} itens"
    :globalFilterFields="lazy ? undefined : tableCols?.map((item) => item.field)"
  >
    <template #empty> Nenhum registro encontrado. </template>
    <template #header v-if="haveHeader || $slots?.header">
      <slot
        v-if="$slots?.header"
        name="header"
        :filterValue="filters"
        :setFilterValue="(value, col = 'global') => setFilter(value, col)"
      />
      <div class="flex p-4" v-else>
        <div class="mr-auto flex items-center gap-2">
          <div class="flex-items-center relative">
            <component
              :is="InputText"
              v-model="filters.global.value"
              placeholder="Pesquisar"
              aria-label="Global Search"
              class=""
              @update:model-value="onSearchInput"
            />
            <component
              :is="IconSearch"
              class="absolute bg-white pointer-events-none top-1/2 right-2 transform -translate-y-1/2 w-5 h-5 text-gray-500"
            />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <component
            :is="Button"
            class="button"
            label="Adicionar"
            @click="createFunction()"
          >
            <component :is="IconAddCircle" />
            Adicionar
          </component>
          <component
            :is="Button"
            class="button-icon"
            @click="refreshFunction()"
          >
            <component :is="IconRefresh" />
          </component>
        </div>
      </div>
    </template>
    <component
      :is="Column"
      v-if="selectionMode"
      :selectionMode="selectionMode"
      headerStyle="width: 3rem"
    ></component>
    <component
      :is="Column"
      v-for="(data, i) in tableCols"
      :field="data.field"
      :header="data.title"
      sortable
      filter
      :style="{ width: data.width, maxWidth: data.maxWidth }"
      :key="i"
    >
      <template #body="slotProps">
        <slot
          v-if="$slots[data.field]"
          :name="data.field"
          :data="slotProps.data"
        />
        <div class="flex gap-4 items-center" v-else-if="data?.isFile">
          <img
            v-if="handleSelectFileIcon(slotProps.data.name)"
            :src="handleSelectFileIcon(slotProps.data.name).iconClass"
            :style="handleSelectFileIcon(slotProps.data.name).style"
          />
          <div class="">
            <p class="m-0 text-ellipsis overflow-hidden">
              {{ slotProps.data.name }}
            </p>
            <p class="m-0">{{ formatBytes(slotProps.data.size) }}</p>
          </div>
        </div>
        <span
          v-else-if="data.field.toLowerCase() === 'id'"
          :style="{ color: '#0053A1' }"
        >
          {{ slotProps.data[data.field] }}
        </span>

        <span
          v-else
          :class="[
            fillStatusLabel(slotProps.data[data.field]),
            data?.customClass,
            'ellipsis-text',
          ]"
          v-tooltip="cellTooltipText(slotProps.data[data.field])"
          :style="{ maxWidth: data.maxWidth || 'auto' }"
        >
          {{ slotProps.data[data.field] }}
        </span>
      </template>
      <template #sorticon="{ sorted, sortOrder }">
        <span class="bh-ml-3 bh-sort bh-flex bh-items-center id asc">
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
            <polygon
              :class="{
                'text-black-second': sorted && sortOrder === -1,
              }"
              points="3.11,6.25 10.89,6.25 7,1.75"
              fill="currentColor"
              class="bh-text-black/20"
            ></polygon>
            <polygon
              :class="{
                'text-black-second': sorted && sortOrder === 1,
              }"
              points="7,12.25 10.89,7.75 3.11,7.75"
              fill="currentColor"
              class="bh-text-black/20"
            ></polygon>
          </svg>
        </span>
      </template>
    </component>
    <component
      :is="Column"
      v-if="rowActions || $slots?.rowActions"
      alignFrozen="right"
      frozen
      :header="!refreshFunction ? 'Ações' : ''"
    >
      <template #header v-if="refreshFunction">
        Ações
        <button
          v-if="!(haveHeader || $slots?.header)"
          @click="refreshFunction()"
          class="text-primary-second hover:text-primary ml-6"
        >
          <component :is="iconRefreshTable" />
        </button>
      </template>
      <template #body="slotProps">
        <div v-if="!$slots?.rowActions" class="flex gap-4 items-center mr-1">
          <span
            v-for="(button, i) of rowActions"
            :key="i"
            class="inline-flex items-center"
            v-tooltip.bottom="rowActionTooltipLabel(button, slotProps.data)"
          >
            <component
              :is="Button"
              :class="`text-primary-second hover:text-primary  static ${
                typeof button.customClass === 'function'
                  ? button.customClass(slotProps.data)
                  : button.customClass
              }`"
              @click="button.onPress(slotProps.data)"
              type="button"
              :disabled="
                typeof button?.disabled === 'function'
                  ? button.disabled(slotProps.data)
                  : !!button?.disabled
              "
            >
              <component
                :is="
                  typeof button.icon === 'function'
                    ? button.icon(slotProps.data)
                    : button.icon
                "
              />
            </component>
          </span>
        </div>
        <slot name="rowActions" :data="slotProps.data" v-else />
      </template>
    </component>
  </component>
</template>
<script>
import iconRefreshTable from "./icon/icon-refresh-table.vue";
import IconSearch from "./icon/icon-search.vue";
import IconAddCircle from "./icon/icon-add-circle.vue";
import IconRefresh from "./icon/icon-refresh.vue";
import { handleSelectFileIcon } from "../service/evidence-icons";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { primeTooltipString } from "../utils/primeTooltipString.js";

export default {
  name: "UiTable",
  props: {
    tableItems: {
      type: Array,
      required: true,
    },
    tableCols: {
      type: Array,
      required: true,
    },
    rowActions: {
      type: Array,
      required: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    paginator: {
      type: Boolean,
      required: false,
      default: true,
    },
    refreshFunction: {
      type: Function,
      required: false,
    },
    haveHeader: {
      type: Boolean,
      required: false,
    },
    createFunction: {
      type: Function,
      required: false,
    },
    selectionMode: {
      type: String,
      required: false,
    },
    selection: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Paginação/busca/ordenação no servidor: cada mudança emite `lazy-load`
     * com { first, rows, sortField, sortOrder, search, filters }, e quem usa o
     * componente responde atualizando `tableItems` e `totalRecords`.
     */
    lazy: {
      type: Boolean,
      required: false,
      default: false,
    },
    totalRecords: {
      type: Number,
      required: false,
      default: 0,
    },
    rows: {
      type: Number,
      required: false,
      default: 20,
    },
    searchDebounce: {
      type: Number,
      required: false,
      default: 300,
    },
  },
  data() {
    return {
      filters: {
        global: {
          value: null,
        },
      },
      first: 0,
      rowsPerPage: this.rows,
      sortFieldValue: null,
      sortOrderValue: null,
      searchTimer: null,
    };
  },
  emits: ["update:selection", "lazy-load"],
  computed: {
    selectedRows: {
      get() {
        return this.selection;
      },
      set(value) {
        this.$emit("update:selection", value);
      },
    },
  },
  methods: {
    emitLazyLoad() {
      this.$emit("lazy-load", {
        first: this.first,
        rows: this.rowsPerPage,
        sortField: this.sortFieldValue,
        sortOrder: this.sortOrderValue,
        search: this.filters.global.value || "",
        filters: this.columnFilters(),
      });
    },
    /**
     * Filtros de coluna achatados em `{ campo: [valor, ...] }`. Aceita tanto a
     * constraint do PrimeVue (`{ value, matchMode }`) quanto o objeto cru que
     * os headers customizados deste repo passam.
     */
    columnFilters() {
      const out = {};
      for (const [field, filter] of Object.entries(this.filters)) {
        if (field === "global") continue;
        const values = (filter?.constraints || [])
          .map((item) => (item && typeof item === "object" ? item.value : item))
          .filter((value) => value !== null && value !== undefined && value !== "");
        if (values.length) out[field] = values;
      }
      return out;
    },
    onPage(event) {
      if (!this.lazy) return;
      this.first = event.first;
      this.rowsPerPage = event.rows;
      this.emitLazyLoad();
    },
    onSort(event) {
      if (!this.lazy) return;
      this.sortFieldValue = event.sortField;
      this.sortOrderValue = event.sortOrder;
      this.first = 0;
      this.emitLazyLoad();
    },
    onSearchInput() {
      if (!this.lazy) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.first = 0;
        this.emitLazyLoad();
      }, this.searchDebounce);
    },
    cellTooltipText(value) {
      return primeTooltipString(value);
    },
    rowActionTooltipLabel(button, row) {
      const raw =
        typeof button.label === "function" ? button.label(row) : button.label;
      return primeTooltipString(raw);
    },
    fillStatusLabel(status) {
      switch (status) {
        case "Ativo":
        case "Desbloqueado":
          return "badge font-normal rounded-[10px] text-success border-success";
        case "Inativo":
        case "Bloqueado":
          return "badge font-normal rounded-[10px] text-danger border-danger";
        default:
          return "";
      }
    },
    setFilter(value, col) {
      if (col === "global") {
        this.filters.global.value = value;
        this.onSearchInput();
      } else {
        this.filters[col].constraints = value;
        if (this.lazy) {
          this.first = 0;
          this.emitLazyLoad();
        }
      }
    },
    formatBytes(bytes) {
      if (bytes === 0) return "0 Bytes";

      const sizes = ["Bytes", "kB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      const value = bytes / Math.pow(1024, i);

      // Ajuste para ter 2 casas decimais
      return `${value.toFixed(2)} ${sizes[i]}`;
    },
  },
  created() {
    this.tableCols.forEach((col) => {
      this.filters[col.field] = {
        operator: "or",
        constraints: [],
      };
    });
  },
  mounted() {
    // Em modo não-lazy quem carrega a primeira página é o consumidor.
    if (this.lazy) this.emitLazyLoad();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  setup() {
    return {
      iconRefreshTable,
      handleSelectFileIcon,
      DataTable,
      Column,
      InputText,
      Button,
      IconSearch,
      IconAddCircle,
      IconRefresh,
    };
  },
};
</script>
<style>
.panel-row-per-page-dropdown {
  z-index: 9203 !important;
}
</style>